import type {
  WizardData,
  AdminAlert,
  ClinicalAlert,
  AdminResult,
  ClinicalResult,
  Decision,
} from "@/types/wizard";

const num = (v: string): number => Number(v || 0);

export function computeAdminAlerts(d: WizardData): AdminAlert[] {
  const alerts: AdminAlert[] = [];

  if (!d.programador.trim()) {
    alerts.push({ severidad: "subsanable", titulo: "No se registró quién programó", accion: "Completar trazabilidad de programación.", penalizaProgramador: false, evidence: "Campo 'programador' vacío" });
  }
  if (!d.doc.trim() || !d.nombre.trim() || !d.cirugia.trim() || !d.fecha.trim()) {
    const faltantes = [
      !d.doc.trim() && "doc",
      !d.nombre.trim() && "nombre",
      !d.cirugia.trim() && "cirugía",
      !d.fecha.trim() && "fecha",
    ].filter(Boolean).join(", ");
    alerts.push({ severidad: "no-subsanable", titulo: "Datos básicos de programación incompletos", accion: "Reprogramar por causa administrativa — datos del paciente incompletos.", penalizaProgramador: true, evidence: `Faltan: ${faltantes}` });
  }
  if (d.orden === "no") {
    alerts.push({ severidad: "no-subsanable", titulo: "Orden quirúrgica ausente o vencida", accion: "Reprogramar por causa administrativa.", penalizaProgramador: true, evidence: "Orden médica marcada 'No'" });
  }
  if (d.copago === "no") {
    alerts.push({ severidad: "subsanable", titulo: "Copago / cuota moderadora pendiente", accion: "Gestionar pago antes del procedimiento.", penalizaProgramador: false, evidence: "Copago marcado 'No'" });
  }
  if (d.insumo === "no") {
    alerts.push({ severidad: "no-subsanable", titulo: "Insumos no disponibles", accion: "Reprogramar por causa administrativa.", penalizaProgramador: true, evidence: "Insumo marcado 'No'" });
  }
  if (d.autorizacion === "no") {
    alerts.push({ severidad: "no-subsanable", titulo: "Procedimiento sin autorización", accion: "Reprogramar por causa administrativa — gestionar autorización.", penalizaProgramador: true, evidence: "Autorización marcada 'No'" });
  }
  if (d.acompanante === "no") {
    alerts.push({ severidad: "subsanable", titulo: "Sin acompañante responsable", accion: "Verificar presencia de acompañante antes del procedimiento.", penalizaProgramador: false, evidence: "Acompañante marcado 'No'" });
  }
  // Menor de edad requiere identificación del acompañante (dato médico-legal).
  if (d.esMenorEdad && !d.nombreAcompanante.trim()) {
    alerts.push({ severidad: "no-subsanable", titulo: "Menor de edad sin acompañante identificado", accion: "Registrar nombre y parentesco del adulto responsable antes del procedimiento.", penalizaProgramador: false, evidence: "esMenorEdad=true, nombreAcompanante vacío" });
  }
  if (d.ayuno === "no") {
    alerts.push({ severidad: "no-subsanable", titulo: "Ayuno incumplido", accion: "Reprogramar — el paciente no cumple el tiempo de ayuno requerido.", penalizaProgramador: false, evidence: "Ayuno marcado 'No'" });
  }
  if (d.suspensionMedConfirm === "no") {
    alerts.push({ severidad: "subsanable", titulo: "Suspensión de medicamentos no confirmada", accion: "Confirmar con el paciente la suspensión de medicamentos indicada antes de la cirugía.", penalizaProgramador: false, evidence: "Confirmación suspensión = 'No'" });
  }
  if (d.dispPostop === "no") {
    alerts.push({ severidad: "no-subsanable", titulo: "Sin disponibilidad para postoperatorio", accion: "Reprogramar — el paciente no puede asistir al postoperatorio.", penalizaProgramador: false, evidence: "Disponibilidad postop = 'No'" });
  }

  return alerts;
}

export function computeClinicalAlerts(d: WizardData): ClinicalAlert[] {
  const alerts: ClinicalAlert[] = [];
  const ps = num(d.ps);
  const pd = num(d.pd);
  const temp = num(d.temp);
  const glicemia = num(d.glicemia);

  // Negociables (amarillo)
  if (d.blefaritis === "leve")               alerts.push({ clasificacion: "negociable", titulo: "Blefaritis leve", accion: "Continuar con manejo y validación clínica.", evidence: "Blefaritis = 'leve'" });
  if (d.resp === "leve")                     alerts.push({ clasificacion: "negociable", titulo: "Síntomas respiratorios leves sin fiebre", accion: "Requiere consulta con anestesia.", evidence: "Respiratorio = 'leve'" });
  if (d.piel === "herida_seca")              alerts.push({ clasificacion: "negociable", titulo: "Herida seca o cicatriz sin infección", accion: "Registrar y continuar con revisión clínica.", evidence: "Piel = 'herida_seca'" });
  if (d.piel === "dermatitis_no_infectada")  alerts.push({ clasificacion: "negociable", titulo: "Dermatitis no infectada", accion: "Registrar y continuar con revisión clínica.", evidence: "Piel = 'dermatitis_no_infectada'" });
  if (d.dental === "deterioro_sin_infeccion")alerts.push({ clasificacion: "negociable", titulo: "Deterioro dental sin infección", accion: "Puede continuar con registro del hallazgo.", evidence: "Dental = 'deterioro_sin_infeccion'" });
  if (d.dental === "dentadura_riesgo")       alerts.push({ clasificacion: "negociable", titulo: "Dentadura deteriorada con riesgo infeccioso", accion: "Requiere consulta con anestesia.", evidence: "Dental = 'dentadura_riesgo'" });
  if ((ps >= 160 && ps <= 179) || (pd >= 100 && pd <= 109)) alerts.push({ clasificacion: "negociable", titulo: "Presión arterial entre 160/100 y 179/109", accion: "Requiere consulta con anestesia.", evidence: `PA = ${ps}/${pd} mmHg (rango negociable: 160-179 / 100-109)` });
  if (glicemia >= 181 && glicemia <= 200)    alerts.push({ clasificacion: "negociable", titulo: "Glicemia entre 181 y 200 mg/dL", accion: "Requiere consulta con anestesia.", evidence: `Glicemia = ${glicemia} mg/dL (rango negociable: 181-200)` });
  if (d.conjuntivitis === "alergica_leve")   alerts.push({ clasificacion: "negociable", titulo: "Conjuntivitis alérgica leve", accion: "Continuar tratamiento habitual y validación clínica.", evidence: "Conjuntivitis = 'alergica_leve'" });
  if (d.piel === "hongos_piel_unas")         alerts.push({ clasificacion: "negociable", titulo: "Hongos en piel y uñas", accion: "Registrar; coordinar manejo dermatológico.", evidence: "Piel = 'hongos_piel_unas'" });
  if (d.piel === "caspa_seborreica")         alerts.push({ clasificacion: "negociable", titulo: "Caspa seborreica", accion: "Continuar — registrar hallazgo.", evidence: "Piel = 'caspa_seborreica'" });

  // No negociables (rojo)
  if (d.blefaritis === "severa")             alerts.push({ clasificacion: "no-negociable", titulo: "Blefaritis activa moderada o severa", accion: "Reprogramar e iniciar manejo clínico.", evidence: "Blefaritis = 'severa'" });
  if (temp > 37.5)                           alerts.push({ clasificacion: "no-negociable", titulo: "Fiebre o cuadro febril reciente", accion: "Reprogramar y derivar a valoración médica.", evidence: `Temperatura = ${temp}°C (umbral: >37.5°C)` });
  if (d.resp === "severo")                   alerts.push({ clasificacion: "no-negociable", titulo: "Síntomas respiratorios moderados o severos", accion: "Reprogramar y remitir a valoración médica.", evidence: "Respiratorio = 'severo'" });
  if (d.piel === "herida_infectada")         alerts.push({ clasificacion: "no-negociable", titulo: "Herida infectada o úlcera activa", accion: "Reprogramar e iniciar manejo.", evidence: "Piel = 'herida_infectada'" });
  if (d.piel === "herida_abierta")           alerts.push({ clasificacion: "no-negociable", titulo: "Herida abierta o úlcera activa", accion: "Reprogramar y remitir.", evidence: "Piel = 'herida_abierta'" });
  if (d.piel === "celulitis")                alerts.push({ clasificacion: "no-negociable", titulo: "Celulitis orbitaria/periorbitaria activa", accion: "Remisión urgente y reprogramación.", evidence: "Piel = 'celulitis'" });
  if (d.piel === "ulcera_mmii")              alerts.push({ clasificacion: "no-negociable", titulo: "Úlcera en miembros inferiores", accion: "Reprogramar — derivar a medicina general / EPS.", evidence: "Piel = 'ulcera_mmii'" });
  if (d.dental === "infeccion_activa")       alerts.push({ clasificacion: "no-negociable", titulo: "Infección dental activa", accion: "Reprogramar hasta resolución clínica.", evidence: "Dental = 'infeccion_activa'" });
  if (ps >= 180 || pd >= 110)                alerts.push({ clasificacion: "no-negociable", titulo: "Presión arterial no controlada", accion: "Reprogramar y remitir a valoración médica.", evidence: `PA = ${ps}/${pd} mmHg (umbral: PS>=180 o PD>=110)` });
  if (glicemia > 200)                        alerts.push({ clasificacion: "no-negociable", titulo: "Glicemia mayor de 200 mg/dL", accion: "Reprogramar hasta control metabólico.", evidence: `Glicemia = ${glicemia} mg/dL (umbral: >200)` });
  if (d.conjuntivitis === "activa")          alerts.push({ clasificacion: "no-negociable", titulo: "Conjuntivitis activa", accion: "Reprogramar y tratar previamente.", evidence: "Conjuntivitis = 'activa'" });

  // Anticoagulantes
  if (d.anticoagulante === "aspirina" && d.aspirinaSuspendida === "no") {
    alerts.push({ clasificacion: "no-negociable", titulo: "Aspirina/ASA no suspendida 5 días antes", accion: "Validar con anestesiología. Procede solo si es cirugía sin sedación general — evaluar caso a caso.", evidence: "Anticoagulante=aspirina, aspirinaSuspendida=no" });
  }
  if (["clopidogrel", "warfarina", "apixaban", "otro"].includes(d.anticoagulante) && d.autorizAnestAnticoag !== "si") {
    alerts.push({ clasificacion: "no-negociable", titulo: "Anticoagulantes sin autorización anestesiológica", accion: "Confirmar autorización del anestesiólogo. Si no hay, reprogramar o coordinar con anestesia.", evidence: `Anticoagulante=${d.anticoagulante}, autorización anestesiología=${d.autorizAnestAnticoag}` });
  }
  if (d.suspNoAutorizadaMedEsenciales) {
    alerts.push({ clasificacion: "negociable", titulo: "Suspensión no autorizada de medicamentos esenciales", accion: "Revisar cifras de tensión / glicemia. Coordinar ajuste con médico tratante o anestesiólogo.", evidence: "Checkbox 'suspensión no autorizada' marcado" });
  }

  return alerts;
}

export function evaluateAdminStatus(d: WizardData): AdminResult {
  const alertas = computeAdminAlerts(d);
  const noSubsanables = alertas.filter((a) => a.severidad === "no-subsanable");
  const subsanables = alertas.filter((a) => a.severidad === "subsanable");
  if (noSubsanables.length > 0) {
    return { estado: "pendiente_no_subsanable", texto: "PENDIENTE ADMINISTRATIVO NO SUBSANABLE", detalle: "No puede cerrarse como apto. Reprogramar.", alertas };
  }
  if (subsanables.length > 0) {
    return { estado: "pendiente_subsanable", texto: "PENDIENTE ADMINISTRATIVO SUBSANABLE", detalle: "Debe subsanarse antes del cierre.", alertas };
  }
  return { estado: "completo", texto: "ADMINISTRATIVO COMPLETO", detalle: "Sin novedades administrativas.", alertas: [] };
}

export function evaluateClinicalStatus(d: WizardData): ClinicalResult {
  const alertas = computeClinicalAlerts(d);
  const noNegociables = alertas.filter((a) => a.clasificacion === "no-negociable");
  const negociables = alertas.filter((a) => a.clasificacion === "negociable");
  if (noNegociables.length > 0) {
    return { semaforo: "rojo", texto: "REPROGRAMAR POR CAUSA CLÍNICA", detalle: "Alertas clínicas no negociables detectadas.", alertas };
  }
  if (negociables.length > 0) {
    return { semaforo: "amarillo", texto: "REQUIERE GESTIÓN CON ANESTESIA", detalle: "Alertas clínicas negociables que requieren evaluación.", alertas };
  }
  return { semaforo: "verde", texto: "APTO CLÍNICAMENTE", detalle: "Sin alertas clínicas automáticas.", alertas: [] };
}

/** Valida precondiciones antes de guardar. "" = válido. */
export function validateClose(d: WizardData, admin: AdminResult, clinical: ClinicalResult): string {
  const requiereGestionAutomatica = admin.estado === "pendiente_subsanable" || clinical.semaforo === "amarillo";
  const decisionFinal = d.decisionFinalEvaluador;

  if (!decisionFinal) return "Debes seleccionar la decisión final del evaluador.";

  if (requiereGestionAutomatica) {
    if (d.requiereGestion !== "si") return "Este caso requiere gestión del evaluador antes del cierre.";
    if (!d.tipoGestion) return "Debes seleccionar el tipo de gestión.";
    if (!d.accionesEvaluador.trim()) return "Debes registrar las acciones realizadas por el evaluador.";
    if ((d.tipoGestion === "consulta_anestesia" || d.tipoGestion === "ambas") && !d.profesionalConsultado.trim()) {
      return "Debes registrar el profesional o área consultada.";
    }
    if ((d.tipoGestion === "consulta_anestesia" || d.tipoGestion === "ambas") && !d.respuestaRecibida.trim()) {
      return "Debes registrar la respuesta o indicación recibida.";
    }
    if (!d.evidenciaCierre.trim()) return "Debes registrar la evidencia de cierre.";
  }

  const ambasCausas = admin.estado === "pendiente_no_subsanable" && clinical.semaforo === "rojo";
  if (ambasCausas) {
    if (decisionFinal !== "Reprogramar por causa clínica") {
      return "Existen alertas clínicas y administrativas. La decisión debe ser 'Reprogramar por causa clínica' (la causa administrativa queda registrada en las alertas del caso).";
    }
  } else {
    if (admin.estado === "pendiente_no_subsanable" && decisionFinal !== "Reprogramar por causa administrativa") {
      return "Si existe pendiente administrativo no subsanable, la decisión debe ser 'Reprogramar por causa administrativa'.";
    }
    if (clinical.semaforo === "rojo" && decisionFinal !== "Reprogramar por causa clínica") {
      return "Si existe alerta clínica no negociable, la decisión debe ser 'Reprogramar por causa clínica'.";
    }
  }
  if (admin.estado === "completo" && clinical.semaforo === "verde" &&
      decisionFinal !== "Apto" && decisionFinal !== "Reemplazar con paciente de lista de espera") {
    return "El paciente no tiene alertas. La decisión final debe ser 'Apto' o 'Reemplazar con paciente de lista de espera'.";
  }
  return "";
}

/** DTO listo para POST /evaluations (el backend lo estampa con evaluador y fecha). */
export function buildEvaluationDto(
  d: WizardData,
  admin: AdminResult,
  clinical: ClinicalResult,
  decision: Decision
) {
  return {
    doc: d.doc,
    nombre: d.nombre,
    edad: d.edad,
    eps: d.eps,
    medicoTratante: d.medicoTratante,
    tipoAnestesia: d.tipoAnestesia,
    cirugia: d.cirugia,
    fecha: d.fecha,
    telefono: d.telefono,
    programador: d.programador,
    diagnostico: d.diagnostico,
    cie10: d.cie10,
    diagnosticosExtra: d.diagnosticosExtra,
    // Cadena legible para PDF/Excel: "H25.1 · Catarata senil | H40.1 · Glaucoma…"
    diagnosticosResumen: [
      d.cie10 && d.diagnostico ? `${d.cie10} · ${d.diagnostico}` : null,
      ...d.diagnosticosExtra.map((dx) => `${dx.code} · ${dx.description}`),
    ].filter(Boolean).join(" | "),
    observaciones: d.observaciones,
    antecedentes: d.antecedentes,
    medicamentos: d.medicamentos,
    alergias: d.alergias,
    cirugiasPrevias: d.cirugiasPrevias,
    administrativos: {
      orden: d.orden, copago: d.copago,
      insumo: d.insumo, insumoDetalle: d.insumoDetalle,
      autorizacion: d.autorizacion,
      acompanante: d.acompanante, esMenorEdad: d.esMenorEdad, nombreAcompanante: d.nombreAcompanante,
      ayuno: d.ayuno,
      suspensionMedConfirm: d.suspensionMedConfirm, dispPostop: d.dispPostop,
      recMedAntihipertensivos: d.recMedAntihipertensivos,
      recMedTiroidea: d.recMedTiroidea,
    },
    antecedentes_detallados: {
      diabetes: d.antDiabetes, hipertension: d.antHipertension, tiroidea: d.antTiroidea,
      renal: d.antRenal, gripa: d.antGripa,
      cardio: d.antCardio, cardio_cual: d.antCardioCual,
      pulmonar: d.antPulmonar, pulmonar_cual: d.antPulmonarCual,
      acv: d.antACV, infartos: d.antInfartos, tromboembolismo: d.antTromboembolismo,
      retraso_mental: d.antRetrasoMental, down: d.antDown, colostomia: d.antColostomia,
      crisis_ansiosa: d.antCrisisAnsiosa, otra: d.antOtra,
      anticoagulante: d.anticoagulante, anticoagulante_otro: d.anticoagulanteOtro,
      aspirina_suspendida: d.aspirinaSuspendida, autoriz_anest_anticoag: d.autorizAnestAnticoag,
      susp_previas_descontrol: d.suspPreviasDescontrol,
      susp_no_autorizada_med_esenciales: d.suspNoAutorizadaMedEsenciales,
    },
    clinicos: {
      ps: d.ps, pd: d.pd, paControlada: d.paControlada,
      fc: d.fc, saturacion: d.saturacion, temp: d.temp,
      glicemia: d.glicemia, glicemiaControlada: d.glicemiaControlada,
      peso: d.peso,
      blefaritis: d.blefaritis, resp: d.resp, piel: d.piel,
      dental: d.dental, conjuntivitis: d.conjuntivitis, estadoHigienico: d.estadoHigienico,
    },
    chequeo: {
      modalidad: d.modalidad,
      orientComprende: d.orientComprende, orientHoraAntic: d.orientHoraAntic,
      orientSignosAlarma: d.orientSignosAlarma,
    },
    gestionEvaluador: {
      requiereGestion: d.requiereGestion, tipoGestion: d.tipoGestion,
      accionesEvaluador: d.accionesEvaluador,
      profesionalConsultado: d.profesionalConsultado,
      respuestaRecibida: d.respuestaRecibida,
      evidenciaCierre: d.evidenciaCierre,
      decisionFinalEvaluador: decision,
    },
    estadoAdministrativo: admin.texto,
    resultadoClinico: clinical.texto,
    accionFinal: decision,
    alertasAdministrativas: admin.alertas,
    alertasClinicas: clinical.alertas,
  };
}

export const DEFAULT_WIZARD_DATA: WizardData = {
  doc: "", nombre: "", edad: "", eps: "", medicoTratante: "", tipoAnestesia: "",
  cirugia: "", fecha: "", telefono: "", programador: "",
  diagnostico: "", cie10: "", diagnosticosExtra: [], observaciones: "",
  orden: "si", copago: "si", insumo: "si", insumoDetalle: "",
  autorizacion: "si", acompanante: "si", esMenorEdad: false, nombreAcompanante: "",
  ayuno: "si", suspensionMedConfirm: "si", dispPostop: "si",
  recMedAntihipertensivos: false, recMedTiroidea: false,
  ps: "", pd: "", paControlada: "si", fc: "", saturacion: "", temp: "",
  glicemia: "", glicemiaControlada: "si", peso: "",
  antecedentes: "", medicamentos: "", alergias: "", cirugiasPrevias: "",
  antDiabetes: false, antHipertension: false, antTiroidea: false, antRenal: false,
  antGripa: false, antCardio: false, antCardioCual: "",
  antPulmonar: false, antPulmonarCual: "",
  antACV: false, antInfartos: false, antTromboembolismo: false,
  antRetrasoMental: false, antDown: false, antColostomia: false, antCrisisAnsiosa: false,
  antOtra: "", anticoagulante: "ninguno", anticoagulanteOtro: "",
  aspirinaSuspendida: "na", autorizAnestAnticoag: "na",
  suspPreviasDescontrol: false, suspNoAutorizadaMedEsenciales: false,
  blefaritis: "ninguna", resp: "ninguno", piel: "ninguna",
  dental: "ninguno", conjuntivitis: "ninguna", estadoHigienico: "adecuado",
  modalidad: "presencial", orientComprende: "si", orientHoraAntic: "si", orientSignosAlarma: "si",
  requiereGestion: "no", tipoGestion: "", accionesEvaluador: "",
  profesionalConsultado: "", respuestaRecibida: "", evidenciaCierre: "",
  decisionFinalEvaluador: "",
};
